import type { Effect, EffectOf, EffectType } from "./document";

/**
 * Effects and layer styles: defaults, rendering order and scaling. The engine applies effects
 * in stack order within four fixed stages, so the visible list always matches what's drawn:
 * blur → interior styles (overlays, inner shadow/glow, satin, bevel) → strokes → shadows/glows
 * (each cast by everything before it).
 */
export const EFFECT_STAGES: readonly (readonly EffectType[])[] = [
  ["blur"],
  ["colorOverlay", "gradientOverlay", "patternOverlay", "satin", "innerGlow", "innerShadow", "bevel"],
  ["stroke"],
  ["shadow", "glow"],
];

export const effectStage = (t: EffectType): number => EFFECT_STAGES.findIndex((s) => s.includes(t));

/** Absent `enabled` means on (effects saved before the toggle existed). */
export const effectEnabled = (e: Effect): boolean => e.enabled !== false;

/** Stable sort into rendering order. */
export function orderEffects<T extends Effect>(effects: readonly T[]): T[] {
  return effects.map((e, i) => [e, i] as const).sort((a, b) => effectStage(a[0].type) - effectStage(b[0].type) || a[1] - b[1]).map(([e]) => e);
}

/** Where a new effect of `type` goes so the stack stays in rendering order (end of its stage). */
export function insertEffectIndex(effects: readonly Effect[], type: EffectType): number {
  const st = effectStage(type);
  let i = 0;
  while (i < effects.length && effectStage(effects[i].type) <= st) i++;
  return i;
}

/** Photoshop-like defaults for a newly added style (`assetId` only matters for patterns). */
export function defaultEffect<T extends EffectType>(type: T, assetId = ""): EffectOf<T> {
  const all: { [K in EffectType]: EffectOf<K> } = {
    shadow: { type: "shadow", color: "#00000066", x: 8, y: 12, blur: 24 },
    glow: { type: "glow", color: "#000000b3", size: 24 },
    blur: { type: "blur", radius: 8 },
    stroke: { type: "stroke", position: "outside", width: 4, paint: { type: "solid", color: "#1f2329ff" } },
    innerShadow: { type: "innerShadow", color: "#000000bf", x: 4, y: 6, blur: 10, choke: 0, blendMode: "multiply" },
    innerGlow: { type: "innerGlow", color: "#000000bf", size: 16, source: "edge", choke: 0, blendMode: "normal" },
    bevel: { type: "bevel", style: "inner", direction: "up", depth: 100, size: 8, soften: 0, angle: 120, altitude: 30, highlight: "#ffffffbf", shadow: "#000000bf" },
    colorOverlay: { type: "colorOverlay", color: "#ff3b30ff", blendMode: "normal" },
    gradientOverlay: {
      type: "gradientOverlay",
      paint: { type: "linear", angle: 90, stops: [{ offset: 0, color: "#ffffffff" }, { offset: 1, color: "#000000ff" }] },
      opacity: 1,
      blendMode: "normal",
    },
    patternOverlay: { type: "patternOverlay", assetId, scale: 1, opacity: 1, blendMode: "normal" },
    satin: { type: "satin", color: "#00000080", angle: 19, distance: 11, size: 14, invert: true, blendMode: "multiply" },
  };
  return structuredClone(all[type]) as EffectOf<T>;
}

const cap = (v: number) => Math.min(500, v);

/** An effect with its distances scaled by `k` (resize-with-content). */
export function scaleEffect(e: Effect, k: number): Effect {
  switch (e.type) {
    case "shadow":
    case "innerShadow":
      return { ...e, x: e.x * k, y: e.y * k, blur: cap(e.blur * k) };
    case "glow":
    case "innerGlow":
      return { ...e, size: cap(e.size * k) };
    case "blur":
      return { ...e, radius: cap(e.radius * k) };
    case "stroke":
      return { ...e, width: cap(e.width * k) };
    case "bevel":
      return { ...e, size: cap(e.size * k), soften: Math.min(50, e.soften * k) };
    case "satin":
      return { ...e, distance: cap(e.distance * k), size: cap(e.size * k) };
    case "patternOverlay":
      return { ...e, scale: Math.min(100, Math.max(0.01, e.scale * k)) };
    case "colorOverlay":
    case "gradientOverlay":
      return e;
  }
}

/** How far an effect can paint outside the element's silhouette, in px (for raster padding). */
export function effectReach(e: Effect): number {
  if (!effectEnabled(e)) return 0;
  switch (e.type) {
    case "shadow":
      return Math.hypot(e.x, e.y) + e.blur * 1.5;
    case "glow":
      return e.size * 1.6;
    case "blur":
      return e.radius * 1.5;
    case "stroke":
      return e.position === "inside" ? 0 : e.position === "center" ? e.width / 2 + 1 : e.width + 1;
    case "bevel":
      return e.style === "inner" ? 0 : e.size * 1.5 + e.soften;
    default:
      return 0;
  }
}

/** Assets an effect stack depends on (pattern images). */
export const effectAssets = (effects: readonly Effect[]): string[] => effects.flatMap((e) => (e.type === "patternOverlay" ? [e.assetId] : []));
